import { classNames } from "@andorhealth/clientuireact.designsystem";
import { CheckGlyph } from "./CheckGlyph";
import { MicGlyph } from "./MicGlyph";
import { STATE_LABEL } from "./constants";
import type { WakeOrbProps } from "./types";
import { useOrbCanvas } from "./useOrbCanvas";
import "./WakeOrb.css";

/**
 * Voice assistant orb, painted on a canvas.
 *
 * Figma: speaking `20168:41428`, listening `20168:41714`, mic `20172:41823`
 * (file `N8HyyrHrICcdIZLzOIazro`). `listening` and `speaking` are the two
 * states specified by design; `idle`, `connecting` and `processing` are
 * provisional treatments built from the same knobs in `constants.ts`.
 *
 * Pass `level` from `MicrophoneCapture`'s `MicFrame.level` (RMS 0..1) — in
 * `listening` it drives the speed, length, width and brightness of the light
 * travelling around the rim.
 */
export function WakeOrb({
  state,
  size = "medium",
  level = 0,
  className,
  label,
}: WakeOrbProps) {
  const { hostRef, canvasRef } = useOrbCanvas({ state, size, level });

  const decorative = label === "";
  const accessibleName = label ?? STATE_LABEL[state];

  return (
    <div
      ref={hostRef}
      className={classNames(
        "wake-orb",
        `wake-orb--${size}`,
        `wake-orb--${state}`,
        className,
      )}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : accessibleName}
      aria-hidden={decorative || undefined}
      data-state={state}
    >
      <canvas ref={canvasRef} className="wake-orb__canvas" />
      <div className="wake-orb__success" aria-hidden />
      <MicGlyph className="wake-orb__mic" />
      <CheckGlyph className="wake-orb__check" />
    </div>
  );
}
